import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';

import { ILibraryCuisineType } from '../../queries/fragments/libraryCuisineType';
import { ILibraryOpeningHours } from '../../queries/fragments/libraryOpeningHours';
import { ILibrarySocialMediaLinks } from '../../queries/fragments/librarySocialMediaLinks';
import {
  IMeetingplacePageHomeMinifiedExtendedAttributeFilters,
  IMeetingplacePageHomeSeoRichMinifiedExtendedHomePage,
} from '../../queries/fragments/meetingPlacePageHome-minified';
import { IMeetingPlacePageTenantFoodBevItem } from '../../queries/fragments/meetingPlacePageTenantFoodBevItem';
import { IMeetingPlacePageTenantShopItem } from '../../queries/fragments/meetingPlacePageTenantShopItem';
import { generateCorrectSlug } from '../generate-correct-slug';
import { getTenantCardCategories } from '../list-page/list-page-helpers';
import { getSeoDescription } from '../seo-description';
import {
  DaysOfWeek,
  IItemListSchema,
  IItemListTenantTypes,
  IListItemSchema,
  ILocalBusinessSchema,
  IMeetingPlacePageTenantFoodBevListSchemaData,
  IMeetingPlacePageTenantShopListSchemaData,
  IMettingPlacePageSchemaData,
  IOpeningHoursSpecificationSchema,
} from './interfaces/schema.inteface';

export const createSeoSchema = (
  data: IMettingPlacePageSchemaData,
): ILocalBusinessSchema | IItemListSchema | null => {
  return getSeoSchema(data);
};

export const getSeoSchema = (
  data: IMettingPlacePageSchemaData,
): ILocalBusinessSchema | IItemListSchema | null => {
  if (pageIsHomepage(data)) {
    return createHomePageSeoSchema(data);
  }
  if (pageIsShopList(data)) {
    return createTenantSchema('Store', data?.shopPages, data?.homepage);
  }
  if (pageIsFoodBeverageList(data)) {
    return createTenantSchema(
      'FoodEstablishment',
      data?.foodBeveragePages,
      data?.homepage,
    );
  }
  return null;
};

export const createHomePageSeoSchema = (
  page: IMeetingplacePageHomeSeoRichMinifiedExtendedHomePage,
): ILocalBusinessSchema => {
  const meetingPlace = page?.meetingPlace;
  const seoImageUrl = meetingPlace?.logo?.file?.url
    ? `https:${meetingPlace?.logo?.file?.url}`
    : undefined;

  const schema = {
    '@context': 'http://schema.org',
    '@type': 'ShoppingCenter',
    name: meetingPlace?.name,
    description: getSeoDescription(page?.seoDescription?.seoDescription),
    url: meetingPlace?.webUrl,
    logo: seoImageUrl,
    telephone: meetingPlace?.phoneNumber,
    email: meetingPlace?.eMailAddress,
    geo: {
      '@type': 'GeoCoordinates',
      latitude: meetingPlace?.coordinates?.lat,
      longitude: meetingPlace?.coordinates?.lon,
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: meetingPlace?.visitorsAddressStreet,
      addressLocality: meetingPlace?.visitorsAddressCity,
      postalCode: meetingPlace?.visitorsAddressZipCode,
      addressCountry: meetingPlace?.country?.name,
    },
    openingHoursSpecification: createOpeningHoursList(
      meetingPlace?.openingHours,
    ),
  };

  return schema;
};

const createTenantSchema = (
  type: IItemListTenantTypes,
  pages:
    | IMeetingPlacePageTenantShopItem[]
    | IMeetingPlacePageTenantFoodBevItem[],
  homePage: IMeetingplacePageHomeMinifiedExtendedAttributeFilters,
): IItemListSchema => {
  const itemList = pages?.map(
    (
      item:
        | IMeetingPlacePageTenantShopItem
        | IMeetingPlacePageTenantFoodBevItem,
      index: number,
    ) => createListItem(item, index, type, homePage),
  );
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: itemList,
  };
  return schema;
};

const createListItem = (
  item: IMeetingPlacePageTenantShopItem | IMeetingPlacePageTenantFoodBevItem,
  index: number,
  type: IItemListTenantTypes,
  homePage: IMeetingplacePageHomeMinifiedExtendedAttributeFilters,
): IListItemSchema => {
  const name = item.name ? item.name : item.brand?.name || '';
  const slug = item.slug ? item.slug : item.brand?.slug || '';
  const coverImageUrl =
    item?.coverImage?.file?.url || item?.brand?.coverImage?.file?.url || '';
  const description =
    item.description?.raw || item.brand?.description?.raw || '';

  const listItem = {
    '@type': 'ListItem',
    position: index,
    item: {
      '@type': type,
      name: name,
      image: `https:${coverImageUrl}`,
      description:
        description && documentToPlainTextString(JSON.parse(description)),
      url:
        (process.env.SITE_URL || process.env.GATSBY_SITE_URL) +
        generateCorrectSlug({
          customizedSlug: item.customizedSlug,
          name,
          node_locale: item.node_locale,
          slug,
        }),
      brand: {
        '@type': 'Brand',
        name: item.brand?.name || '',
        logo: `https:${item.brand?.coverImage?.file?.url}`,
      },

      ...(getTenantCategories(item)?.length && {
        category: getTenantCategories(item),
      }),

      ...(item?.openingHours && {
        openingHoursSpecification: createOpeningHoursList(item?.openingHours),
      }),

      ...(item.contactDetails?.raw && {
        contactPoint: {
          '@type': 'ContactPoint',
          description: documentToPlainTextString(
            JSON.parse(item.contactDetails?.raw),
          ),
        },
      }),

      ...(item.socialMediaLinks &&
        getSocialMediaLinks(item.socialMediaLinks).length > 0 && {
          socialMedia: {
            '@type': 'SocialMediaPosting',
            sameAs: getSocialMediaLinks(item.socialMediaLinks),
          },
        }),

      ...('priceLevel' in item && { priceRange: item.priceLevel?.name }),

      ...('cuisine' in item && {
        servesCuisine: item.cuisine?.map(
          (type: ILibraryCuisineType) => type.name,
        ),
      }),
      ...(homePage?.meetingPlace && {
        address: {
          '@type': 'PostalAddress',
          streetAddress: homePage?.meetingPlace?.visitorsAddressStreet,
          addressLocality: homePage?.meetingPlace?.visitorsAddressCity,
          postalCode: homePage?.meetingPlace?.visitorsAddressZipCode,
          addressCountry: homePage?.meetingPlace?.country?.name,
        },
      }),
    },
  };
  return listItem;
};

const pageIsHomepage = (
  page: IMettingPlacePageSchemaData,
): page is IMeetingplacePageHomeSeoRichMinifiedExtendedHomePage => {
  return page.__typename === 'ContentfulMeetingPlacePageHome';
};

const pageIsShopList = (
  page: IMettingPlacePageSchemaData,
): page is IMeetingPlacePageTenantShopListSchemaData => {
  return page.__typename === 'ContentfulMeetingPlacePageTenantShopList';
};

const pageIsFoodBeverageList = (
  page: IMettingPlacePageSchemaData,
): page is IMeetingPlacePageTenantFoodBevListSchemaData => {
  return page.__typename === 'ContentfulMeetingPlacePageTenantFoodBeverageList';
};

const createOpeningHoursList = (
  schedule: ILibraryOpeningHours,
): IOpeningHoursSpecificationSchema[] => {
  const daysOfWeek: DaysOfWeek[] = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  const openingHours = [];
  let currentOpeningHours = null;

  for (const dayOfWeek of daysOfWeek) {
    const opens = (schedule as any)[`${dayOfWeek.toLowerCase()}Open`];
    const closes = (schedule as any)[`${dayOfWeek.toLowerCase()}Close`];

    if (
      currentOpeningHours &&
      currentOpeningHours.opens === opens &&
      currentOpeningHours.closes === closes
    ) {
      currentOpeningHours.dayOfWeek.push(dayOfWeek);
    } else {
      if (currentOpeningHours) {
        openingHours.push(currentOpeningHours);
      }

      currentOpeningHours = {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [dayOfWeek],
        opens: opens,
        closes: closes,
      };
    }
  }

  if (currentOpeningHours) {
    openingHours.push(currentOpeningHours);
  }
  return openingHours;
};

const getSocialMediaLinks = (
  socialMediaUrls: ILibrarySocialMediaLinks,
): string[] => {
  const { id, ...socialMediaWithoutId } = socialMediaUrls;
  return Object.values(socialMediaWithoutId)
    .filter((value) => value !== null)
    .filter((value) => typeof value !== 'undefined');
};

const getTenantCategories = (
  page: IMeetingPlacePageTenantShopItem | IMeetingPlacePageTenantFoodBevItem,
): string[] => {
  const categories = getTenantCardCategories(page);
  return categories.length > 0
    ? categories.map((category) => category.name)
    : [];
};
